import React from "react";
import { Grid, FormControlLabel, LinearProgress } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  fetchDefaultDocuments,
  updateDefaultDocument,
  DefaultDocument,
  DefaultDocumentsResponse,
} from "./queries";
import { useStyles } from "./styles";
import { useSubscription } from "../../../../../framework/src/contexts/Subscription";
import { PhaseTwoSwitch } from "../../../../../components/src/ModifiedComponents.web";
import CustomTypography from "../../../../../components/src/CustomTypography.web";
import withToast, {
  withToastProps,
} from "../../../../../components/src/withSnackBar.Web";

const QUERY_KEY = ["defaultDocuments"];

type ToggleVariables = {
  documentKey: string;
  enabled: boolean;
};

type DefaultDocumentsProps = withToastProps;

const DefaultDocuments = ({ showToast }: DefaultDocumentsProps) => {
  const classes = useStyles();
  const { authToken } = useSubscription();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchDefaultDocuments(authToken),
    enabled: !!authToken,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    onError: () => {
      showToast({
        type: "error",
        message: "Failed to load default documents",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ documentKey, enabled }: ToggleVariables) =>
      updateDefaultDocument(authToken, documentKey, enabled),
    onMutate: async ({ documentKey, enabled }: ToggleVariables) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<DefaultDocumentsResponse>(QUERY_KEY);
      queryClient.setQueryData<DefaultDocumentsResponse>(QUERY_KEY, (old) => ({
        data: (old?.data ?? []).map((document) =>
          document.document_key === documentKey ? { ...document, enabled } : document
        ),
      }));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
      showToast({
        type: "error",
        message: "Failed to update — change reverted",
      });
    },
    onSuccess: (response: DefaultDocumentsResponse) => {
      queryClient.setQueryData(QUERY_KEY, response);
    },
  });

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isError) {
    return <Alert severity="error">Failed to load default documents.</Alert>;
  }

  const documents = data?.data ?? [];

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <CustomTypography color="textSecondary" fontSize="0.9rem" padding="0.25rem 0 0.75rem">
          These documents are automatically added to every new client's signature flow.
          Turning one off only affects new flows going forward — it won't remove the
          document from flows already in progress.
        </CustomTypography>
      </Grid>

      {documents.length === 0 && (
        <Grid item xs={12}>
          <CustomTypography color="textSecondary" padding="0.5rem 0">
            No default documents configured.
          </CustomTypography>
        </Grid>
      )}

      {documents.map((document: DefaultDocument) => (
        <Grid item xs={12} className={classes.row} key={document.document_key}>
          <FormControlLabel
            classes={{ root: classes.formControlLabel, label: classes.formControlLabelText }}
            control={
              <PhaseTwoSwitch
                checked={document.enabled}
                color="primary"
                onChange={(event) =>
                  toggleMutation.mutate({
                    documentKey: document.document_key,
                    enabled: event.target.checked,
                  })
                }
              />
            }
            label={
              <>
                <CustomTypography color="secondary" fontWeight={600} padding="0 0.5rem">
                  {document.title}
                </CustomTypography>
                <CustomTypography color="textSecondary" fontSize="0.85rem" padding="0 0.5rem">
                  {document.description}
                </CustomTypography>
              </>
            }
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default withToast(DefaultDocuments);
